const MINUS_SIGN = "−";
const EN_DASH = "–";
const exact = (nf) => nf;
const formatters = /* @__PURE__ */ new Map();
function numberFormat(locale, options) {
  const key = `${locale}|${JSON.stringify(options)}`;
  let nf = formatters.get(key);
  if (!nf) {
    nf = new Intl.NumberFormat(locale, options);
    formatters.set(key, nf);
  }
  return nf;
}
function assertDigits(d) {
  if (!Number.isInteger(d) || d < 0 || d > 8) throw new RangeError("fractionDigits must be 0..8");
}
function minorDigits(currency, locale = "en") {
  return numberFormat(locale, { style: "currency", currency }).resolvedOptions().maximumFractionDigits ?? 2;
}
function isWellFormedCurrency(currency) {
  return /^[A-Za-z]{3}$/u.test(currency);
}
function digitsOf(opts) {
  const d = opts.fractionDigits ?? (isWellFormedCurrency(opts.currency) ? minorDigits(opts.currency, opts.locale) : 2);
  assertDigits(d);
  return d;
}
function toDecimal(valueMinor, digits) {
  if (!Number.isSafeInteger(valueMinor)) throw new RangeError(`Not a safe integer amount: ${String(valueMinor)}`);
  const neg = valueMinor < 0;
  const s = (neg ? -BigInt(valueMinor) : BigInt(valueMinor)).toString();
  if (digits === 0) return { neg, abs: s };
  const padded = s.padStart(digits + 1, "0");
  return { neg, abs: `${padded.slice(0, -digits)}.${padded.slice(-digits)}` };
}
const UNKNOWN_CURRENCY = "XXX";
function displayFormatter(opts, digits) {
  const display = opts.currencyDisplay ?? "symbol";
  const base = { minimumFractionDigits: digits, maximumFractionDigits: digits, useGrouping: true };
  const code = opts.currency.trim();
  if (display === "none" || code === "") return { nf: numberFormat(opts.locale, base), literalCode: null };
  if (!isWellFormedCurrency(code)) {
    return {
      nf: numberFormat(opts.locale, { ...base, style: "currency", currency: UNKNOWN_CURRENCY, currencyDisplay: "code" }),
      literalCode: code
    };
  }
  return { nf: numberFormat(opts.locale, { ...base, style: "currency", currency: code, currencyDisplay: display }), literalCode: null };
}
function formatMinor(valueMinor, opts) {
  const digits = digitsOf(opts);
  const { neg, abs } = toDecimal(valueMinor, digits);
  if (valueMinor === 0 && opts.zeroStyle === "dash") return EN_DASH;
  if (valueMinor === 0 && opts.zeroStyle === "blank") return "";
  const { nf, literalCode } = displayFormatter(opts, digits);
  const minus = neg && opts.signStyle !== "parens";
  if (!minus && literalCode === null) {
    const plain = exact(nf).format(abs);
    return neg ? `(${plain})` : plain;
  }
  const text = exact(nf).formatToParts(minus ? `-${abs}` : abs).map((p) => p.type === "minusSign" ? MINUS_SIGN : p.type === "currency" && literalCode !== null ? literalCode : p.value).join("");
  return neg && !minus ? `(${text})` : text;
}
function describeMinor(valueMinor, opts) {
  return formatMinor(valueMinor, { ...opts, signStyle: "minus", zeroStyle: "number" });
}
function formatMinorForEdit(valueMinor, opts) {
  const digits = digitsOf(opts);
  const { neg, abs } = toDecimal(valueMinor, digits);
  const nf = numberFormat(opts.locale, { minimumFractionDigits: digits, maximumFractionDigits: digits, useGrouping: false });
  return (neg ? "-" : "") + exact(nf).format(abs);
}
function currencySymbol(currency, locale, display = "symbol") {
  if (!isWellFormedCurrency(currency)) return currency.trim();
  const parts = numberFormat(locale, { style: "currency", currency, currencyDisplay: display }).formatToParts(1);
  return parts.find((p) => p.type === "currency")?.value ?? currency;
}
const separatorCache = /* @__PURE__ */ new Map();
function localeSeparators(locale) {
  let s = separatorCache.get(locale);
  if (s) return s;
  const parts = numberFormat(locale, { minimumFractionDigits: 1, useGrouping: true }).formatToParts(12345675e-1);
  const rawGroup = parts.find((p) => p.type === "group")?.value ?? "";
  const dec = parts.find((p) => p.type === "decimal")?.value ?? ".";
  const group = /* @__PURE__ */ new Set([" "]);
  if (rawGroup && !/^\s$/u.test(rawGroup)) group.add(rawGroup);
  if (rawGroup === "’") group.add("'");
  const decimal = /* @__PURE__ */ new Set([dec]);
  if (dec === "," && !group.has(".")) decimal.add(".");
  s = { group, decimal };
  separatorCache.set(locale, s);
  return s;
}
function fold(token) {
  return token.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().replace(/\.+$/u, "");
}
const tokenCache = /* @__PURE__ */ new Map();
function acceptedTokens(currency, locale) {
  const key = `${currency}|${locale}`;
  let set = tokenCache.get(key);
  if (set) return set;
  const out = /* @__PURE__ */ new Set([fold(currency)]);
  for (const display of ["symbol", "narrowSymbol", "code"]) {
    try {
      out.add(fold(currencySymbol(currency, locale, display)));
    } catch {
    }
  }
  set = out;
  tokenCache.set(key, set);
  return set;
}
const TOKEN = "[\\p{L}\\p{Sc}][\\p{L}\\p{Sc}.]*";
const LEAD_SIGN_RE = /^([+\-−])\s*/u;
const PREFIX_RE = new RegExp(`^(${TOKEN})\\s*`, "u");
const SUFFIX_RE = new RegExp(`\\s*(${TOKEN})$`, "u");
const ANY_TOKEN_RE = new RegExp(TOKEN, "gu");
function parseUnsigned(text, sep, digits) {
  let decimalAt = -1;
  for (let i = 0; i < text.length; i++) {
    if (sep.decimal.has(text[i] ?? "")) {
      if (decimalAt !== -1) return null;
      decimalAt = i;
    }
  }
  const intStr = decimalAt === -1 ? text : text.slice(0, decimalAt);
  const fracStr = decimalAt === -1 ? "" : text.slice(decimalAt + 1);
  if (decimalAt !== -1 && fracStr === "") return null;
  if (!/^\d*$/u.test(fracStr) || fracStr.length > digits) return null;
  let intDigits;
  if (intStr === "") {
    if (fracStr === "") return null;
    intDigits = "0";
  } else if (/^\d+$/u.test(intStr)) {
    intDigits = intStr;
  } else {
    const segments = [];
    let cur = "";
    for (const ch of intStr) {
      if (ch >= "0" && ch <= "9") cur += ch;
      else if (sep.group.has(ch)) {
        segments.push(cur);
        cur = "";
      } else return null;
    }
    segments.push(cur);
    const last = segments.length - 1;
    const ok = segments.every((g, i) => i === 0 ? g.length >= 1 && g.length <= 3 : i === last ? g.length === 3 : g.length === 2 || g.length === 3);
    if (!ok) return null;
    intDigits = segments.join("");
  }
  return BigInt(intDigits) * 10n ** BigInt(digits) + BigInt(fracStr.padEnd(digits, "0") || "0");
}
function toSafe(neg, minor) {
  if (minor > BigInt(Number.MAX_SAFE_INTEGER)) return null;
  const n = Number(minor);
  return neg && n !== 0 ? -n : n;
}
function parseAmount(input, opts, digits) {
  let t = input;
  if (t === EN_DASH || t === "—") return 0;
  let neg = false;
  const paren = /^\((.*)\)$/u.exec(t);
  if (paren) {
    neg = true;
    t = (paren[1] ?? "").trim();
  }
  const accepted = acceptedTokens(opts.currency, opts.locale);
  let sign;
  let m = LEAD_SIGN_RE.exec(t);
  if (m) {
    sign = m[1];
    t = t.slice(m[0].length);
  }
  let hasPrefix = false;
  if (m = PREFIX_RE.exec(t)) {
    if (!accepted.has(fold(m[1] ?? ""))) return null;
    hasPrefix = true;
    t = t.slice(m[0].length);
  }
  if (sign === void 0 && (m = LEAD_SIGN_RE.exec(t))) {
    sign = m[1];
    t = t.slice(m[0].length);
  }
  if (m = SUFFIX_RE.exec(t)) {
    if (hasPrefix || !accepted.has(fold(m[1] ?? ""))) return null;
    t = t.slice(0, t.length - m[0].length);
  }
  if (sign !== void 0) {
    if (paren) return null;
    neg = sign !== "+";
  }
  t = t.trim();
  if (t === "") return null;
  const minor = parseUnsigned(t, localeSeparators(opts.locale), digits);
  return minor === null ? null : toSafe(neg, minor);
}
const CURRENCY_MARK = "\0";
function evaluate(input, opts, digits) {
  const accepted = acceptedTokens(opts.currency, opts.locale);
  let bad = input.includes(CURRENCY_MARK);
  const text = input.replace(ANY_TOKEN_RE, (tok) => {
    if (!accepted.has(fold(tok))) bad = true;
    return CURRENCY_MARK;
  });
  if (bad) return null;
  const sep = localeSeparators(opts.locale);
  const isNumChar = (c) => c >= "0" && c <= "9" || sep.group.has(c) || sep.decimal.has(c);
  const scale = 10n ** BigInt(digits);
  const toks = [];
  let pendingPrefix = false;
  let i = 0;
  while (i < text.length) {
    const c = text[i] ?? "";
    if (c === " ") {
      i++;
    } else if (c === CURRENCY_MARK) {
      const prev = toks[toks.length - 1];
      if (prev && "num" in prev) {
        if (prev.withCurrency) return null;
        prev.withCurrency = true;
      } else if (pendingPrefix) return null;
      else pendingPrefix = true;
      i++;
    } else if ("+-−*×/÷()".includes(c)) {
      const op = c === "−" ? "-" : c === "×" ? "*" : c === "÷" ? "/" : c;
      if (pendingPrefix && op !== "-" && op !== "+") return null;
      toks.push({ op });
      i++;
    } else if (isNumChar(c)) {
      let j = i;
      while (j < text.length && isNumChar(text[j] ?? "")) j++;
      const minor2 = parseUnsigned(text.slice(i, j).trim(), sep, digits);
      if (minor2 === null) return null;
      toks.push({ num: { n: minor2, d: scale }, withCurrency: pendingPrefix });
      pendingPrefix = false;
      i = j;
    } else return null;
  }
  if (pendingPrefix) return null;
  let pos = 0;
  const peek = () => {
    const t = toks[pos];
    return t && "op" in t ? t.op : void 0;
  };
  const expr = () => {
    let left = term();
    while (left && (peek() === "+" || peek() === "-")) {
      const op = peek();
      pos++;
      const right = term();
      if (!right) return null;
      left = { n: op === "+" ? left.n * right.d + right.n * left.d : left.n * right.d - right.n * left.d, d: left.d * right.d };
    }
    return left;
  };
  const term = () => {
    let left = factor();
    while (left && (peek() === "*" || peek() === "/")) {
      const op = peek();
      pos++;
      const right = factor();
      if (!right) return null;
      if (op === "*") left = { n: left.n * right.n, d: left.d * right.d };
      else {
        if (right.n === 0n) return null;
        const sign = right.n < 0n ? -1n : 1n;
        left = { n: left.n * right.d * sign, d: left.d * right.n * sign };
      }
    }
    return left;
  };
  const factor = () => {
    const t = toks[pos];
    if (!t) return null;
    if ("num" in t) {
      pos++;
      return t.num;
    }
    if (t.op === "-" || t.op === "+") {
      pos++;
      const f = factor();
      return f && t.op === "-" ? { n: -f.n, d: f.d } : f;
    }
    if (t.op === "(") {
      pos++;
      const e = expr();
      if (!e || peek() !== ")") return null;
      pos++;
      return e;
    }
    return null;
  };
  const result = expr();
  if (!result || pos !== toks.length) return null;
  const num = result.n * scale;
  const neg = num < 0n;
  const absNum = neg ? -num : num;
  const minor = (2n * absNum + result.d) / (2n * result.d);
  return toSafe(neg, minor);
}
function parseMinor(text, opts) {
  const digits = digitsOf(opts);
  const t = text.replace(/\s+/gu, " ").trim();
  if (t === "") return null;
  const simple = parseAmount(t, opts, digits);
  if (simple !== null || !opts.allowExpression) return simple;
  return evaluate(t, opts, digits);
}
export {
  EN_DASH,
  MINUS_SIGN,
  currencySymbol,
  describeMinor,
  formatMinor,
  formatMinorForEdit,
  localeSeparators,
  minorDigits,
  parseMinor
};
//# sourceMappingURL=money.js.map
